import { Link, useParams, useSearchParams, useOutletContext } from "react-router-dom"
import { useComputed, useSignal } from "@preact/signals-react"

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Button from 'react-bootstrap/Button'
import DropdownButton from 'react-bootstrap/DropdownButton'
import Dropdown from 'react-bootstrap/Dropdown'
import Spinner from 'react-bootstrap/Spinner'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'

import ConfirmationModal from "../../components/ConfirmationModal"
import DebugAccordion from "../../components/DebugAccordion"
import Icon from "../../components/Icon.jsx"
import PageTitle from "../../components/PageTitle.jsx"
import ProfileNav from "./components/ProfileNav"
import ProfileTools from "./components/ProfileTools.jsx";
import { NotificationTypes, addNotification } from "../../stores/notification-store"
import { UpdateProfile } from "../../utils/api.js";
import { NOT_FOUND } from "../../utils/constants.js"

import useProfile from "../../hooks/useProfile.js"
import useProfileSchema, { profileSchema } from "../../hooks/useProfileSchema.js"
import useSchemaManifest from "../../hooks/useSchemaManifest.js"

export default function ProfileApiInterfaceList() {

    const { beId, profileId } = useParams()
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const { config, accessToken, canEditSignal, accordionData: sidebarAccordionData  } = useOutletContext()
    const { manifest } = useSchemaManifest()

    const { profile, loading: profileLoading, isLoaded: profileIsLoaded, modified, ccschema } = useProfile(beId, profileId, config, accessToken)
    const apiInterfaces = useComputed(() => {
        if (profile.value.componentProfile) {
            // extract apiInterface from profile for ease of use
            const interfaces = profile.value?.componentProfile?.config?.apiInterfaceConfig?.apiInterface || []
            return interfaces
        }
        return []
    })

    const { loading: schemaLoading, isLoaded: schemaIsLoaded, version: profileVersion } = useProfileSchema(beId, ccschema.value, manifest.value, config, accessToken)
    const apiInterfaceDropdownItems = useComputed(() => {
        if (profileSchema.value.properties) {
            const apiInterfaceItems = profileSchema.value?.properties?.config?.properties?.apiInterfaceConfig?.properties?.apiInterface?.items ?? []
            const apiInterfaceOptions = apiInterfaceItems.map(s => {
                const apiInterfaceName = s.properties.cdType["s4-caption"]
                const id = s.properties.cdType["s4-model-key"]
                return {
                    apiInterfaceName,
                    id
                }
            })
            return apiInterfaceOptions
        }
        return []
    })
 

    const showDeleteModal = useSignal(false)
    const confirmCdType = useSignal('')
    const confirmTaskDesc = useSignal('')
    const confirmListenPort = useSignal('')
    const accordionData = [
        { name: 'API Interfaces', data: apiInterfaces },
        { name: 'Component Profile', data: profile },
        { name: 'API Interfaces Dropdown data', data: apiInterfaceDropdownItems },
        { name: 'Profile Schema', data: profileSchema },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config },
        { name: 'Schema Manifest', data: manifest }
    ]

    function ApiInterfaceTableRows({ apiInterfaces }) {
        let rows = []
        apiInterfaces.forEach(apiInterface => {
            rows.push(
                <ApiInterfaceRow apiInterface={apiInterface} key={`${apiInterface.cdType}${apiInterface.listenPort}`} />
            )
        })

        return (
            <tbody>{rows}</tbody>
        )
    }

    function ApiInterfaceRow({ apiInterface }) {
        const cdType = apiInterface.cdType
        const taskDesc = apiInterface.taskDesc
        const listenPort = apiInterface.listenPort
        return (
            <tr>
                <td><Link to={`./${apiInterface.cdType}?port=${apiInterface.listenPort}`}>{apiInterface.taskDesc}</Link></td>
                <td>{apiInterface.cdType}</td>
                <td>{apiInterface.listenPort}</td>
                <td>
                    {canEditSignal.value && <Link to={`./${apiInterface.cdType}?port=${apiInterface.listenPort}`}><Button variant="outline-primary"><Icon icon="pencil" width="16" height="16"></Icon> Edit</Button></Link>}
                    {canEditSignal.value && <Button className="mx-2" variant="outline-danger" onClick={() => showModal(cdType, taskDesc, listenPort)}><Icon icon="trash" width="16" height="16"></Icon> Delete</Button>}
                </td>
            </tr>
        )
    }

    function deleteApiInterface() {
        showDeleteModal.value = false

        // filter out apiInterface to be deleted
        const filteredApiInterfaces = profile.value.componentProfile.config.apiInterfaceConfig.apiInterface.filter(apiInterface =>
            !(apiInterface.listenPort == confirmListenPort.value));

        // set newly filtered apiInterface array 
        profile.value.componentProfile.config.apiInterfaceConfig.apiInterface = filteredApiInterfaces

        let data = {
            id: profile.value.componentProfile.id,
            name: profile.value.componentProfile.name,
            businessEntityId: profile.value.componentProfile.businessEntityId,
            config: profile.value.componentProfile.config,
        }
                
        // add a schema value to the config data. This allows us to load this specific schema rather than a default one in future edits
        data.config.utgGlobal.ccschema = profileVersion.value

        console.log(data)

        UpdateProfile(beId, profileId, data, config, accessToken)
            .then(result => {
                modified.value = true
                addNotification('The Api Interface was successfully removed.', NotificationTypes.SUCCESS, true)
                profile.value = result
                window.location.reload()
            })
            .catch(error => {
                console.log(error.toString())
                addNotification(error.toString(), NotificationTypes.ERROR, false)
                return false
            })
            .finally(() => {
                isFormSubmitting.value = false
                // must return false to avoid postback
                return false
            })
    }

    const showModal = (cdType, taskDesc, listenPort) => {
        // just set the state and the modal will appear
        confirmCdType.value = cdType
        confirmTaskDesc.value = taskDesc
        confirmListenPort.value = listenPort
        showDeleteModal.value = true
    }

    function NewApiInterfaceDropdown() {
        // only allow one Oracle Interface per UTG
        let oracleApiInterfaces = apiInterfaces.value.filter( apiInterface => {
            return apiInterface.cdType == 148
        })

        let apiInterfaceOptions = apiInterfaceDropdownItems.value.map(s => {
            let isDisabled = false
            
            if (s.id == 148 && oracleApiInterfaces.length) {
                isDisabled = true
            }

            return (
                <Dropdown.Item href={`/be/${beId}/profiles/${profileId}/apiinterfaces/${s.id}`} key={s.id} disabled={isDisabled}>{s.apiInterfaceName}</Dropdown.Item>
            )
        })

        return (
            <DropdownButton id="dropdown-add-apiinterface"
                variant="outline-secondary"
                title="New API Interface"
                className="mx-3">
                {apiInterfaceOptions}
            </DropdownButton>
        )
    }

    if (!profileIsLoaded.value || !schemaIsLoaded.value) {
        return (
            <div className="d-flex justify-content-center">
                <Spinner className="mt-5 align-center" animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        )
    }

    return (
        <>
        <ConfirmationModal show={showDeleteModal.value} handleCancel={() => showDeleteModal.value = false} handleConfirm={() => deleteApiInterface()} message={`Are you sure you want to delete ${confirmTaskDesc.value}? This action cannot be undone.`} title={`Deleting ${confirmTaskDesc.value}`} />
        <Breadcrumb className="px-3 pt-1 bg-light">
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/profiles` }}>
                Profile List
            </Breadcrumb.Item>
            <Breadcrumb.Item active>Manage {profile.value.componentProfile.name}</Breadcrumb.Item>
        </Breadcrumb>
        <Stack direction="horizontal" gap={3}>
            <PageTitle title='Profile API Interfaces' canEditSignal={canEditSignal} />
            <ProfileTools profile={profile.value}></ProfileTools>
        </Stack>
        <ProfileNav beId={beId} profileId={profile.value.componentProfile.id} activeKey="2" debug={debug}></ProfileNav>

        <Table responsive striped hover size="sm">
            <thead>
                <tr>
                    <th>Description</th>
                    <th>Type</th>
                    <th>Port</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <ApiInterfaceTableRows apiInterfaces={apiInterfaces.value} />
        </Table>
        {canEditSignal.value && <NewApiInterfaceDropdown />}
        <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}